/**
 * Headset-Tasten über WebHID (07.10.2026: Jabra-Headset aus der Station
 * nehmen soll in KMVS annehmen; die Linkus-Desktop-App bleibt geschlossen).
 *
 * Ohne diese Steuerung nahm die Linkus-App den Anruf an, sobald das Headset
 * abgehoben wurde — die Anlage entzog ihn dem Browser, das KMVS-Fenster ging
 * zu. Edge/Chrome lesen die Telefonie-Tasten eines USB-Headsets über WebHID
 * (HID-Usage-Page „Telephony"); KMVS nimmt damit selbst an, legt auf und
 * schaltet stumm, und setzt die Lampen (Klingeln, Abgehoben, Stumm) zurück,
 * damit das Headset weiß, in welchem Zustand das Gespräch ist.
 *
 * Diese Datei ist bewusst ohne React und ohne `navigator.hid` testbar: Das
 * Lesen der Report-Beschreibung, das Erkennen der Tasten und das Bauen der
 * Lampen-Reports sind reine Funktionen. Die Typen bilden nur den Teil von
 * WebHID nach, den KMVS braucht (die DOM-Typen kennen WebHID nicht).
 */

/** HID-Usage als (Seite << 16) | Nummer, wie WebHID sie in `usages` liefert. */
const usage = (seite: number, nr: number) => ((seite << 16) | nr) >>> 0;

/** Telephony-Seite (0x0B) und LED-Seite (0x08) — die Tasten und Lampen eines Headsets. */
export const USAGE = {
    hookSwitch: usage(0x0b, 0x20),
    phoneMute: usage(0x0b, 0x2f),
    ledOffHook: usage(0x08, 0x17),
    ledRing: usage(0x08, 0x18),
    ledMute: usage(0x08, 0x09),
    /** Manche Geräte führen das Klingeln als „Ringer" auf der Telephony-Seite. */
    ringer: usage(0x0b, 0x9e),
} as const;

/** Jabra (GN Audio). Andere Hersteller mit Telephony-Seite funktionieren ebenso. */
export const JABRA_VENDOR_ID = 0x0b0e;
export const TELEPHONY_PAGE = 0x0b;

// ── Minimal-Typen aus WebHID ────────────────────────────────────────────────

export interface HidReportItem {
    usages?: number[];
    usageMinimum?: number;
    usageMaximum?: number;
    isRange?: boolean;
    isAbsolute?: boolean;
    reportSize?: number;
    reportCount?: number;
}
export interface HidReportInfo {
    reportId: number;
    items?: HidReportItem[];
}
export interface HidCollectionInfo {
    usagePage?: number;
    usage?: number;
    inputReports?: HidReportInfo[];
    outputReports?: HidReportInfo[];
    children?: HidCollectionInfo[];
}

/** Wo ein Wert in einem Report steht. `bitOffset` zählt ohne das Report-ID-Byte. */
export interface HidFeld {
    reportId: number;
    bitOffset: number;
    bitSize: number;
    /** false = relative Taste (jeder Druck meldet 1); true = Schalter (1 solange gedrückt/abgehoben). */
    absolut: boolean;
    /** Länge des ganzen Reports in Bytes (nur für Ausgabe-Reports gebraucht). */
    reportBytes: number;
}

export interface HeadsetFelder {
    hook: HidFeld | null;
    mute: HidFeld | null;
    ledOffHook: HidFeld | null;
    ledRing: HidFeld | null;
    ledMute: HidFeld | null;
    /**
     * „Ringer" der Telephony-Seite: lässt das Headset bzw. seine Station selbst
     * klingeln — die Ring-LAMPE blinkt nur (07.10.2026: „die User hören nicht,
     * wenn das Telefon klingelt"). Nicht jedes Modell hat es.
     */
    ringer: HidFeld | null;
}

function usagesDes(item: HidReportItem): number[] {
    if (item.isRange && item.usageMinimum != null && item.usageMaximum != null) {
        const out: number[] = [];
        for (let u = item.usageMinimum; u <= item.usageMaximum && out.length < 256; u++) out.push(u >>> 0);
        return out;
    }
    return (item.usages ?? []).map((u) => u >>> 0);
}

/** Alle Felder eines Reports: Usage → Lage. */
function felderDesReports(report: HidReportInfo): Map<number, HidFeld> {
    const items = report.items ?? [];
    const gesamtBits = items.reduce((s, it) => s + (it.reportSize ?? 0) * (it.reportCount ?? 0), 0);
    const reportBytes = Math.ceil(gesamtBits / 8);
    const out = new Map<number, HidFeld>();
    let offset = 0;
    for (const it of items) {
        const size = it.reportSize ?? 0;
        const count = it.reportCount ?? 0;
        const us = usagesDes(it);
        for (let i = 0; i < count; i++) {
            // Mehr Felder als Usages: die letzte Usage gilt für den Rest (HID-Regel).
            const u = us[Math.min(i, us.length - 1)];
            if (u != null && !out.has(u)) {
                out.set(u, {
                    reportId: report.reportId,
                    bitOffset: offset + i * size,
                    bitSize: size,
                    absolut: it.isAbsolute !== false,
                    reportBytes,
                });
            }
        }
        offset += size * count;
    }
    return out;
}

function allesammeln(cs: readonly HidCollectionInfo[] | undefined, art: "inputReports" | "outputReports"): HidReportInfo[] {
    const out: HidReportInfo[] = [];
    for (const c of cs ?? []) {
        out.push(...(c[art] ?? []));
        out.push(...allesammeln(c.children, art));
    }
    return out;
}

/** Aus der Report-Beschreibung des Geräts die Tasten und Lampen heraussuchen. */
export function findeFelder(collections: readonly HidCollectionInfo[] | undefined): HeadsetFelder {
    const suche = (art: "inputReports" | "outputReports", ...gesucht: number[]): HidFeld | null => {
        for (const r of allesammeln(collections, art)) {
            const f = felderDesReports(r);
            for (const g of gesucht) {
                const treffer = f.get(g);
                if (treffer) return treffer;
            }
        }
        return null;
    };
    return {
        hook: suche("inputReports", USAGE.hookSwitch),
        mute: suche("inputReports", USAGE.phoneMute),
        ledOffHook: suche("outputReports", USAGE.ledOffHook),
        ledRing: suche("outputReports", USAGE.ledRing),
        ledMute: suche("outputReports", USAGE.ledMute),
        ringer: suche("outputReports", USAGE.ringer),
    };
}

/** Ein Bit/Feld aus den Report-Daten lesen (ohne Report-ID-Byte, wie `inputreport.data`). */
export function leseFeld(data: DataView, feld: HidFeld): number {
    let wert = 0;
    for (let b = 0; b < Math.min(feld.bitSize, 32); b++) {
        const bit = feld.bitOffset + b;
        const byte = bit >> 3;
        if (byte >= data.byteLength) break;
        if ((data.getUint8(byte) >> (bit & 7)) & 1) wert |= 1 << b;
    }
    return wert;
}

/** Die Lampen, die KMVS am Headset setzt. */
export interface LampenZustand {
    abgehoben: boolean;
    klingelt: boolean;
    stumm: boolean;
}

/**
 * Die Ausgabe-Reports für einen Lampen-Zustand — je Report-ID einer, mit allen
 * Lampen, die darin liegen. Nicht beschriebene Bits bleiben 0.
 */
export function baueLampenReports(felder: HeadsetFelder, z: LampenZustand): Array<{ reportId: number; data: Uint8Array }> {
    const setzen: Array<[HidFeld | null, boolean]> = [
        [felder.ledOffHook, z.abgehoben],
        [felder.ledRing, z.klingelt],
        [felder.ringer, z.klingelt],
        [felder.ledMute, z.stumm],
    ];
    const reports = new Map<number, Uint8Array>();
    for (const [feld, an] of setzen) {
        if (!feld) continue;
        let data = reports.get(feld.reportId);
        if (!data) {
            data = new Uint8Array(Math.max(1, feld.reportBytes));
            reports.set(feld.reportId, data);
        }
        if (!an) continue;
        const byte = feld.bitOffset >> 3;
        if (byte < data.length) data[byte] |= 1 << (feld.bitOffset & 7);
    }
    return [...reports.entries()].map(([reportId, data]) => ({ reportId, data }));
}

/**
 * Was KMVS dem Headset wirklich schickt (12.10.2026, T-277). Am Jabra hängen
 * oft zwei Programme: KMVS und die Linkus-App. „Abgehoben" ist EIN Zustand
 * im Gerät — schickt KMVS „aufgelegt", während in der App telefoniert wird,
 * legt das Headset dort auf. Darum:
 *
 *  - Gerade verbunden und in KMVS ist nichts los → nichts schicken.
 *  - „Aufgelegt" nur, wenn KMVS selbst vorher „abgehoben" gesetzt hat. Sonst
 *    bleibt der Hook so, wie das Headset ihn meldet (`hookEingang`) — führt
 *    die App ein Gespräch, bleibt es abgehoben; nur das Klingeln geht aus.
 *
 * `bisher` = der zuletzt umgesetzte KMVS-Zustand (null = noch nie).
 * Ergebnis null = nichts senden.
 */
export function zuSendendeLampen(
    z: LampenZustand,
    bisher: LampenZustand | null,
    hookEingang: number,
): LampenZustand | null {
    if (!bisher) {
        if (!z.abgehoben && !z.klingelt && !z.stumm) return null;
    } else if (bisher.abgehoben === z.abgehoben && bisher.klingelt === z.klingelt && bisher.stumm === z.stumm) {
        return null;
    }
    const eigenAbgehoben = !!bisher?.abgehoben;
    const abgehoben = z.abgehoben || (!eigenAbgehoben && hookEingang === 1);
    return { ...z, abgehoben };
}

// ── Tasten → Aktionen ───────────────────────────────────────────────────────

export type HeadsetAktion = "abheben" | "auflegen" | "stumm";

/** Was KMVS über das Gespräch weiß, wenn eine Taste kommt. */
export type GespraechsLage = "frei" | "klingelt" | "aktiv";

/**
 * Aus einem Tastenwechsel die Aktion machen.
 *
 * - Schalter (absolut): 0→1 heißt abgehoben, 1→0 aufgelegt.
 * - Taste (relativ): jeder Druck (0→1) wechselt — klingelt es oder ist frei,
 *   heißt das abheben, läuft ein Gespräch, auflegen.
 *
 * Abheben ohne klingelnden Anruf und Auflegen ohne Gespräch bewirken nichts
 * (`null`): Das Headset aus der Station nehmen, um es aufzusetzen, darf nicht
 * plötzlich wählen oder einen anderen Anruf beenden.
 */
export function hookAktion(
    vorher: number,
    jetzt: number,
    absolut: boolean,
    lage: GespraechsLage,
): HeadsetAktion | null {
    if (vorher === jetzt) return null;
    let abheben: boolean;
    if (absolut) {
        abheben = jetzt === 1;
    } else {
        if (jetzt !== 1) return null; // Loslassen einer Taste
        abheben = lage !== "aktiv";
    }
    if (abheben) return lage === "klingelt" ? "abheben" : null;
    return lage === "aktiv" ? "auflegen" : null;
}

/** Stummtaste: jeder Druck (0→1) schaltet um, nur im Gespräch. */
export function muteAktion(vorher: number, jetzt: number, lage: GespraechsLage): HeadsetAktion | null {
    return vorher !== 1 && jetzt === 1 && lage === "aktiv" ? "stumm" : null;
}
